import React from "react";
import Alert from "../v3/Alert";
import { itemSubmissionRev } from "../../utils/suitabilityRevision";

/**
 * SuitabilityAnswersCard - the investor's answers on a SUITABILITY review item,
 * and whether they are the answers the item is about (API round 15).
 *
 * The page decides whether a decision is offered (see answersMatchItem); this
 * card only says what the reviewer is looking at and, when the decision is
 * held, why.
 *
 * @param {{ answers: ReturnType<import('../../hooks/useSuitabilityAnswers').default>, item: object,
 *            onOpenSuitabilityPage?: Function }} props
 */
export const ANSWERS_UNREADABLE =
  "The investor's answers could not be loaded, so no decision can be recorded on this page. Reload the page.";
export const ANSWERS_OTHER_SUBMISSION =
  "These answers belong to a different submission than this review item: the investor resubmitted the assessment. No decision can be recorded on this item from them; reload the page or go back to the queue.";
export const ANSWERS_STALE_ITEM =
  "This review item is about an earlier submission: the investor has resubmitted the assessment since. Decide the latest answers on the investment's suitability page; deciding there closes this item.";
export const ANSWERS_UNNAMED =
  "This page could not tell which submission these answers belong to, so no decision can be recorded on it. Reload the page.";

/**
 * Are the answers on screen the ones the item is about? They must have been
 * read for the item's own investment (Codex r14 P1: two orders can share a
 * revision number, so the revision alone does not identify them) and belong
 * to the item's submission revision.
 */
export function answersMatchItem(answers, item) {
  const itemRev = itemSubmissionRev(item);
  return (
    !!answers &&
    !!item &&
    answers.status === "ready" &&
    answers.investmentId !== null &&
    answers.investmentId !== undefined &&
    String(answers.investmentId) === String(item.investment_id) &&
    answers.submissionRev !== null &&
    itemRev !== null &&
    answers.submissionRev === itemRev
  );
}

/**
 * Is the ITEM stale (Codex r14 P2)? The answers, read for the item's own
 * investment, belong to a NEWER submission than the item, or the item's
 * revision cannot be read. Its submission is gone, so no decision on it can
 * be right; the direct decide on the suitability page retires it and decides
 * the latest answers (API round 16). Answers OLDER than the item mean the
 * page is stale instead, and a reload fixes that.
 */
export function isStaleItem(answers, item) {
  if (!answers || !item || answers.status !== "ready" || answers.submissionRev === null) return false;
  if (String(answers.investmentId) !== String(item.investment_id)) return false;
  const itemRev = itemSubmissionRev(item);
  return itemRev === null || answers.submissionRev > itemRev;
}

function SuitabilityAnswersCard({ answers, item, onOpenSuitabilityPage }) {
  const stale = isStaleItem(answers, item);
  let notice = null;
  if (answers.status === "error") notice = ANSWERS_UNREADABLE;
  else if (answers.status === "ready" && answers.submissionRev === null) notice = ANSWERS_UNNAMED;
  else if (stale) notice = ANSWERS_STALE_ITEM;
  else if (answers.status === "ready" && !answersMatchItem(answers, item)) notice = ANSWERS_OTHER_SUBMISSION;

  return (
    <section className="card" data-testid="suitability-answers">
      <div className="card__head">
        <div className="card__title">Suitability answers</div>
      </div>
      <div className="card__body">
        {notice && <Alert tone="danger">{notice}</Alert>}
        {stale && onOpenSuitabilityPage && (
          <p>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onOpenSuitabilityPage}>
              Open the suitability page
            </button>
          </p>
        )}
        {answers.status === "loading" && <p className="muted">Loading the investor's answers...</p>}
        {answers.status === "ready" && answers.answers.length === 0 && (
          <p className="muted">No answers are stored for this investor.</p>
        )}
        {answers.status === "ready" && answers.answers.length > 0 && (
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            {answers.answers.map((item, index) => (
              <li key={item.id ?? index} style={{ marginBottom: 10 }}>
                <div>
                  <strong>{item.suitability_assestment_question?.question || "Question not found"}</strong>
                </div>
                <div>
                  Answer: {item.suitability_assestment_option?.option_en || item.user_answer || "-"}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

export default SuitabilityAnswersCard;
